// StudentPromotionPage.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Grid,
  Typography,
  Button,
  Divider,
  Avatar,
  Autocomplete,
  TextField,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Switch,
} from "@mui/material";
import Swal from "sweetalert2";
import api from "../../api";
import { motion } from "framer-motion";
import {
  AccountCircle,
  School,
  ClassOutlined,
  Numbers,
  Call,
  BadgeOutlined,
} from "@mui/icons-material";

const StudentPromotionPage = () => {
  const schoolId = localStorage.getItem("school_id");

  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [rolls, setRolls] = useState([]);
  const [transportLocations, setTransportLocations] = useState([]);

  const [filterClass, setFilterClass] = useState(null);
  const [filterSection, setFilterSection] = useState(null);
  const [filterRoll, setFilterRoll] = useState("");
  const [searchText, setSearchText] = useState("");

  const [promoteData, setPromoteData] = useState({});
  const [feesMap, setFeesMap] = useState({});
  const [usedRolls, setUsedRolls] = useState([]);
  const [promoteSections, setPromoteSections] = useState({});

  // 🔹 Load Initial Data
  useEffect(() => {
    const loadData = async () => {
      try {
        const [clsRes, trRes] = await Promise.all([
          api.get("/get-classes"),
          api.get("/get-tr-location"),
        ]);
        if (clsRes.data?.success) setClasses(clsRes.data.data);
        if (trRes.data?.success) setTransportLocations(trRes.data.data);
      } catch {
        Swal.fire("Error", "Failed to load initial data", "error");
      }
    };
    loadData();
  }, []);

  // 🔹 Load Sections when Class selected
  useEffect(() => {
    if (!filterClass) {
      setSections([]);
      setFilterSection(null);
      setRolls([]);
      setFilterRoll();
      return;
    }

    api
      .get(`/get-sections/${filterClass.id}`)
      .then((res) => {
        if (res.data?.success) {
          setSections(res.data.data);
          setFilterSection(null);
          setRolls([]);
          setFilterRoll();
        } else {
          setSections([]);
          setRolls([]);
        }
      })
      .catch(() => {
        setSections([]);
        setRolls([]);
      });
  }, [filterClass]);

  // Fetch rolls for filter section
  useEffect(() => {
    if (!filterSection?.id) {
      setRolls([]);
      setFilterRoll(null);
      return;
    }

    const fetchRoll = async () => {
      try {
        const res = await api.get(`/get-rolls/${filterSection.id}`);
        if (res.data?.success) {
          setRolls(res.data.data || []);
          setFilterRoll(null);
        } else {
          setRolls([]);
          console.warn("Unexpected response:", res.data);
        }
      } catch (error) {
        setRolls([]);
        console.error("Error fetching rolls:", error);
      }
    };

    fetchRoll();
  }, [filterSection]);

  // 🔹 Filter Students
  const handleFilter = async () => {
    if (!filterClass) {
      Swal.fire("Select Class", "Please select a class", "warning");
      return;
    }

    try {
      const res = await api.post("/students/filter", {
        class_id: filterClass.id,
        section_id: filterSection?.id || "",
        roll_no: filterRoll || "",
      });

      if (res.data?.status) {
        setStudents(res.data.data);
        setFilteredStudents(res.data.data);
      } else {
        setStudents([]);
        setFilteredStudents([]);
        Swal.fire("Info", res.data.message || "No students found", "info");
      }
    } catch {
      Swal.fire("Error", "Failed to fetch students", "error");
    }
  };

  // 🔹 Local search
  useEffect(() => {
    if (!searchText.trim()) setFilteredStudents(students);
    else {
      const q = searchText.toLowerCase();
      setFilteredStudents(
        students.filter(
          (st) =>
            st.name.toLowerCase().includes(q) ||
            st.mobile?.includes(q) ||
            st.student_code?.toLowerCase().includes(q)
        )
      );
    }
  }, [searchText, students]);

  // 🔹 Load Promote Sections for next class
  const loadPromoteSections = async (studentId, classId) => {
    try {
      const res = await api.get(`/get-sections/${classId}`);
      if (res.data?.success)
        setPromoteSections((prev) => ({ ...prev, [studentId]: res.data.data }));
    } catch {
      setPromoteSections((prev) => ({ ...prev, [studentId]: [] }));
    }
  };

  // 🔹 Promote Class select
  const handlePromoteClass = async (student, nextClassId) => {
    setPromoteData((prev) => ({
      ...prev,
      [student.id]: {
        nextClass: nextClassId,
        nextSection: "",
        fees: [],
        roll_no: "",
        transport_location: "",
      },
    }));

    if (!nextClassId) {
      setFeesMap((prev) => ({ ...prev, [student.id]: [] }));
      return;
    }

    loadPromoteSections(student.id, nextClassId);

    try {
      const res = await api.get(`/get-fees/${nextClassId}?school_id=${schoolId}`);
      const validFees = (res.data?.data || []).filter(
        (f) =>
          !["admission", "hostel admission"].includes(f.fee_type.toLowerCase())
      );
      setFeesMap((prev) => ({ ...prev, [student.id]: validFees }));
    } catch {
      setFeesMap((prev) => ({ ...prev, [student.id]: [] }));
    }
  };

  // 🔹 Promote Section select → used rolls fetch
  const handlePromoteSection = async (student, sectionId) => {
    const nextClassId = promoteData[student.id]?.nextClass;
    if (!nextClassId || !sectionId) return;

    setPromoteData((prev) => ({
      ...prev,
      [student.id]: { ...prev[student.id], nextSection: sectionId, roll_no: "" },
    }));

    try {
      const res = await api.get(
        `/students/get-used-rolls/${nextClassId}/${sectionId}`,
        { headers: { "X-School-Id": schoolId } }
      );
      setUsedRolls(res.data?.data || []);
    } catch {
      setUsedRolls([]);
    }
  };

  // 🔹 Fee Check (with conflict)
  const handleFeeCheck = (studentId, feeId) => {
    const selected = promoteData[studentId]?.fees || [];
    const fee = (feesMap[studentId] || []).find((f) => f.id === feeId);
    if (!fee) return;

    const type = fee.fee_type.toLowerCase();
    const isRe = type === "re admission";
    const isHostelRe = type === "hostel re admission";

    if (isRe || isHostelRe) {
      const conflictType = isRe ? "hostel re admission" : "re admission";
      const conflictFee = (feesMap[studentId] || []).find(
        (f) => f.fee_type.toLowerCase() === conflictType
      );
      if (conflictFee && selected.includes(conflictFee.id)) {
        Swal.fire(
          "Conflict",
          "You cannot select both Re-Admission and Hostel Re-Admission.",
          "warning"
        );
        return;
      }
    }

    const updated = selected.includes(feeId)
      ? selected.filter((id) => id !== feeId)
      : [...selected, feeId];

    setPromoteData((prev) => ({
      ...prev,
      [studentId]: { ...prev[studentId], fees: updated },
    }));
  };

  // 🔹 Select All Fees (skip conflicting ones)
  const handleSelectAllFees = (studentId) => {
    const fees = feesMap[studentId] || [];
    if (!fees.length) return;

    const selected = promoteData[studentId]?.fees || [];
    const reAdmission = fees.find(
      (f) => f.fee_type.toLowerCase() === "re admission"
    );
    const hostelRe = fees.find(
      (f) => f.fee_type.toLowerCase() === "hostel re admission"
    );

    let allIds = fees.map((f) => f.id);
    if (reAdmission && hostelRe)
      allIds = allIds.filter((id) => id !== hostelRe.id);

    const allSelected = allIds.every((id) => selected.includes(id));
    const newSelection = allSelected ? [] : allIds;

    setPromoteData((prev) => ({
      ...prev,
      [studentId]: { ...prev[studentId], fees: newSelection },
    }));
  };

  // 🔹 Transport & Roll
  const handleTransportLocationChange = (studentId, locId) => {
    setPromoteData((prev) => ({
      ...prev,
      [studentId]: { ...prev[studentId], transport_location: locId },
    }));
  };

  const handleRollSelect = (studentId, roll) => {
    setPromoteData((prev) => ({
      ...prev,
      [studentId]: { ...prev[studentId], roll_no: roll },
    }));
  };

  const getRollOptions = () =>
    Array.from({ length: 100 }, (_, i) => i + 1).filter(
      (n) => !usedRolls.includes(n)
    );

  // 🔹 Final Promote API
  const handlePromote = async (student) => {
    const data = promoteData[student.id];
    if (!data?.nextClass)
      return Swal.fire("Missing", "Select promotion class", "warning");
    if (!data?.nextSection)
      return Swal.fire("Missing", "Select promotion section", "warning");
    if (!data?.fees?.length)
      return Swal.fire("Missing", "Select at least one fee", "warning");
    if (!data?.roll_no)
      return Swal.fire("Missing", "Select roll number", "warning");

    const transportFee = (feesMap[student.id] || []).find(
      (f) => f.fee_type.toLowerCase() === "transport"
    );
    if (
      transportFee &&
      data.fees.includes(transportFee.id) &&
      !data.transport_location
    ) {
      return Swal.fire(
        "Missing",
        "Please select transport location before promoting.",
        "warning"
      );
    }

    const payload = {
      from_class_id: student.class_id,
      to_class_id: data.nextClass,
      students: [
        {
          student_id: student.id,
          status: "promoted",
          roll_no: data.roll_no,
          fees: (feesMap[student.id] || [])
            .filter((f) => data.fees.includes(f.id))
            .map((f) => ({
              fee_id: f.id,
              amount: f.amount,
              ...(f.fee_type.toLowerCase() === "transport"
                ? { transport_location_id: data.transport_location || null }
                : {}),
            })),
        },
      ],
    };

    try {
      const res = await api.post("/student/promote", payload);
      if (res.data?.status)
        Swal.fire("✅ Success", `${student.name} promoted successfully!`, "success");
      else Swal.fire("Error", res.data.message || "Promotion failed", "error");
    } catch {
      Swal.fire("Error", "Promotion API failed", "error");
    }
  };

  return (
    <Box sx={{ p: 3, background: "#f5f7fa", minHeight: "100vh" }}>
      <Typography variant="h5" mb={3} fontWeight={600}>
        🎓 Student Promotion Panel
      </Typography>

      {/* Filter Section */}
      <Paper sx={{ p: 3, mb: 4, borderRadius: 3, boxShadow: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <Autocomplete
              options={classes}
              getOptionLabel={(o) => o.class_name || ""}
              value={filterClass}
              onChange={(_, v) => setFilterClass(v)}
              renderInput={(p) => <TextField {...p} label="Class" />}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Autocomplete
              options={sections}
              getOptionLabel={(o) => o.section_name || ""}
              value={filterSection}
              onChange={(_, v) => setFilterSection(v)}
              renderInput={(p) => <TextField {...p} label="Section" />}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Autocomplete
              options={rolls}
              getOptionLabel={(option) => option?.roll_no?.toString() || ""}
              value={rolls.find((r) => r.roll_no === filterRoll) || null}
              onChange={(_, value) => setFilterRoll(value?.roll_no || "")}
              renderInput={(params) => <TextField {...params} label="Roll No" />}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              sx={{ height: "56px" }}
              onClick={handleFilter}
            >
              🔍 Search Students
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {students.length > 0 && (
        <TextField
          fullWidth
          placeholder="🔎 Search student by name, mobile or ID..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          sx={{ mb: 3 }}
        />
      )}

      {/* Student Cards */}
      <Grid container spacing={2}>
        {filteredStudents.map((st, i) => {
          const totalFees = feesMap[st.id]?.length || 0;
          const selectedFees = promoteData[st.id]?.fees?.length || 0;
          return (
            <Grid item xs={12} md={4} key={st.id}>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                <Paper sx={{ p: 3, borderRadius: 4, boxShadow: 3 }}>
                  <Typography variant="h6" fontWeight={600} mb={2}>
                    {i + 1}. {st.name.toUpperCase()} ({st.student_code})
                  </Typography>

                  <Avatar
                    src={st.file || "/default-avatar.png"}
                    sx={{ width: 90, height: 90, mx: "auto", mb: 2 }}
                  />

                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ textAlign: "left", pl: 3 }}>
                    <Typography>
                      <BadgeOutlined sx={{ fontSize: 18, mr: 1 }} /> <b>ID:</b> {st.student_code}
                    </Typography>
                    <Typography>
                      <AccountCircle sx={{ fontSize: 18, mr: 1 }} /> <b>Name:</b> {st.name}
                    </Typography>
                    <Typography>
                      <School sx={{ fontSize: 18, mr: 1 }} /> <b>Class:</b> {st.class_name}
                    </Typography>
                    <Typography>
                      <ClassOutlined sx={{ fontSize: 18, mr: 1 }} /> <b>Section:</b>{" "}
                      {st.section_name}
                    </Typography>
                    <Typography>
                      <Numbers sx={{ fontSize: 18, mr: 1 }} /> <b>Roll:</b>{" "}
                      {st.roll_no || "N/A"}
                    </Typography>
                    <Typography>
                      <Call sx={{ fontSize: 18, mr: 1, color: "green" }} /> <b>Mobile:</b>{" "}
                      {st.mobile}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Autocomplete
                        options={classes}
                        getOptionLabel={(opt) => opt.class_name || ""}
                        onChange={(_, v) => handlePromoteClass(st, v?.id)}
                        renderInput={(p) => <TextField {...p} label="Promote To" />}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Autocomplete
                        options={promoteSections[st.id] || []}
                        getOptionLabel={(opt) => opt.section_name || ""}
                        onChange={(_, v) => handlePromoteSection(st, v?.id)}
                        renderInput={(p) => <TextField {...p} label="Section" />}
                      />
                    </Grid>

                    {/* Fees */}
                    <Grid item xs={12}>
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={
                                (promoteData[st.id]?.fees || []).length ===
                                (feesMap[st.id] || []).length &&
                                (feesMap[st.id] || []).length > 0
                              }
                              onChange={() => handleSelectAllFees(st.id)}
                            />
                          }
                          label={`Select All Fees (${selectedFees} of ${totalFees})`}
                        />
                        {(feesMap[st.id] || []).map((fee) => (
                          <React.Fragment key={fee.id}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={(promoteData[st.id]?.fees || []).includes(fee.id)}
                                  onChange={() => handleFeeCheck(st.id, fee.id)}
                                />
                              }
                              label={`${fee.fee_type} (₹${fee.amount})`}
                            />
                            {fee.fee_type.toLowerCase() === "transport" &&
                              (promoteData[st.id]?.fees || []).includes(fee.id) && (
                                <Box sx={{ px: 2 }}>
                                  <Autocomplete
                                    options={transportLocations}
                                    getOptionLabel={(loc) => loc.location_name || ""}
                                    onChange={(_, v) =>
                                      handleTransportLocationChange(st.id, v?.id)
                                    }
                                    renderInput={(p) => (
                                      <TextField {...p} label="Select Location" />
                                    )}
                                  />
                                </Box>
                              )}
                          </React.Fragment>
                        ))}
                      </FormGroup>
                    </Grid>

                    <Grid item xs={6}>
                      <Autocomplete
                        options={getRollOptions()}
                        getOptionLabel={(opt) => opt.toString()}
                        onChange={(_, v) => handleRollSelect(st.id, v)}
                        renderInput={(p) => <TextField {...p} label="Roll No" />}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Button
                        fullWidth
                        variant="contained"
                        color="success"
                        onClick={() => handlePromote(st)}
                      >
                        Promote
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>
              </motion.div>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default StudentPromotionPage;
